// The typed, data-driven nav manifest for the SINGLE sidebar (SEM-5131 owner
// decision 2026-07-30): the hat concept is GONE. There is one menu with
// headed groups — Self-Service / Payroll / HR / Setup — and each
// renders only for callers whose real authority warrants it. The gate fields
// are documented at their declarations below; ./use-visible-menu is the filter
// that reads them.
//
// Self-scope and tenant-scope ROUTES stay separate (/me/requests vs /requests
// show different data) — the merge is in the menu only (SEM-5131 scope note).
//
// Every `labelKey` / `sectionLabelKey` is an i18n key (ADR-006 — no literals).
// `requiredCapability` is UX-only convenience metadata; the route is the
// security gate server-side (ADR-017).

import type { LucideIcon } from "lucide-react";
import {
  UserPlus,
  Award,
  Utensils,
  FileText,
  FileSignature,
  Truck,
  Megaphone,
  Banknote,
  LogOut,
  Scale,
  Boxes,
  BookOpen,
  Building2,
  Home,
  Users,
  Inbox,
  CalendarClock,
  CalendarDays,
  CalendarRange,
  CalendarSearch,
  Hourglass,
  Wallet,
  UserCircle,
  Settings,
  Network,
  Clock,
  Rocket,
  Package,
  HeartHandshake,
  HandHeart,
  ListChecks,
  Bell,
  ShieldCheck,
  KeyRound,
  ClipboardList,
  BarChart3,
  ChartNoAxesCombined,
  LifeBuoy,
  MessageCircleQuestion,
  GitBranch,
  Coins,
  FolderTree,
  Layers,
  MapPin,
  Tags,
  Smartphone,
} from "lucide-react";

// The tier vocabulary is an API concept, not a nav one — it lives beside the
// hook that resolves it (@/api/use-scope-tier) so the rail and the dashboard's
// headcount gate read one definition.
import type { ScopeTier } from "@/api/use-scope-tier";

/**
 * Page-level gates the capability matrix cannot express. Each names ONE
 * predicate that the destination page itself applies, resolved in
 * ./use-visible-menu from the same functions the page imports, so the menu
 * never carries its own copy of the rule.
 *
 * They exist because a matrix cell says "this caller may read the module for
 * SOME rows" (every employee holds most read cells for their own rows), while
 * these pages open only on an exact grant or a JWT role. Gating the entry on
 * the cell alone offered links that answered "no access" (E23).
 */
export type MenuAccessGate =
  | "learning_management"
  | "survey_management"
  | "support_agent"
  | "forms_management"
  | "expenses_management"
  | "policies_management"
  | "policies_self"
  | "feedback_self"
  | "feedback_review"
  | "feedback_manage"
  | "feedback_identity"
  | "staff_requisitions_self"
  | "staff_requisitions_management"
  | "vehicle_requests_management"
  | "document_requests_management"
  | "cafeteria_management"
  | "cafeteria_self"
  | "one_on_ones_self";

interface MenuEntryBase {
  /** Stable id — also the i18n key suffix under `menu.item.*`. */
  readonly key: string;
  readonly labelKey: string;
  readonly icon: LucideIcon;
  /**
   * UX-only capability hint (ADR-014 §1). Documented as non-security: the
   * server independently gates every route (ADR-017). The rail filters by
   * the caller's resolved capabilities. A list means every cell is required
   * (the route checks several cells, e.g. payroll.read AND payroll.edit).
   */
  readonly requiredCapability?: string | readonly string[];
  /**
   * Minimum server-resolved scope tier this destination is reachable at.
   * Needed where the entry sits in a `managementOnly` section (tier >
   * employee_self) but its routes demand MORE than that — the section flag is
   * binary and the capability cell is held by everyone. Same UX-only status as
   * `requiredCapability`; the route is still the security gate.
   */
  readonly requiredTier?: ScopeTier;
  /**
   * The destination page's own access predicate, when it is not a matrix cell.
   * Additive to `requiredCapability` / `requiredTier`: every declared gate must
   * clear.
   */
  readonly requiredAccess?: MenuAccessGate;
}

/**
 * A destination. `children` are its sub-pages: the rail nests them under the
 * entry and opens them while one of them is the current page. One level only —
 * the rail renders no deeper.
 */
export interface MenuLink extends MenuEntryBase {
  readonly href: string;
  readonly children?: readonly MenuLink[];
}

/**
 * A heading-only parent ("İzinlerim") whose pages are all its children. It has
 * no href on purpose: giving it one of its children's pages would put the same
 * address in the rail twice, and the leave list it used to open is the
 * "Taleplerim" screen (SEM-5557).
 */
export interface MenuGroupItem extends MenuEntryBase {
  readonly href?: undefined;
  readonly children: readonly MenuLink[];
}

export type MenuItem = MenuLink | MenuGroupItem;

/** Every entry, parents before their children, in rail order. */
export function flattenMenuItems(items: readonly MenuItem[]): readonly MenuItem[] {
  return items.flatMap((item) => [item, ...(item.children ?? [])]);
}

/** Every destination (entries with an href), parents before their children. */
export function menuLinks(items: readonly MenuItem[]): readonly MenuLink[] {
  return flattenMenuItems(items).filter((item): item is MenuLink => item.href !== undefined);
}

export interface MenuSection {
  /** Stable id — also the i18n key suffix under `menu.section.*`. */
  readonly key: string;
  /** i18n key for the section heading; omit for an ungrouped top section. */
  readonly sectionLabelKey?: string;
  /**
   * Section renders only when the caller's server-resolved scope tier exceeds
   * employee_self (GET /v1/me scope_tier). Needed where per-item capabilities
   * cannot discriminate: every employee legitimately HOLDS the read cells the
   * management screens gate on (leave.read, requests.read, hris.read… —
   * measured on dev persona eng3, 18 allowed cells) for their OWN rows, so the
   * cell alone cannot answer "more than an employee"; scope_tier is the
   * server's answer to exactly that question (the same signal the mobile app's
   * manager tabs use). UX-only, like requiredCapability.
   */
  readonly managementOnly?: boolean;
  /**
   * Sort key for the rail: sections render in DESCENDING rank, and the sort
   * runs over the sections the caller can actually SEE. That is what makes the
   * order role-derived without a second role vocabulary — an İK Direktörü sees
   * hr-management and gets it first; a plain employee never sees that group, so
   * self-service is first for them.
   *
   * Ranks are spaced so a future group can slot in without renumbering.
   */
  readonly authorityRank: number;
  /** `none`: all entries; `preview`: ten entries; `group`: heading toggle. */
  readonly collapseMode?: "none" | "preview" | "group";
  readonly items: readonly MenuItem[];
}

/**
 * The rail's section order. Callers pass the ALREADY-FILTERED (visible)
 * sections — sorting the full manifest would order groups the caller cannot
 * see and change nothing.
 */
export function orderSectionsByAuthority(
  sections: readonly MenuSection[],
): readonly MenuSection[] {
  return [...sections].sort((a, b) => b.authorityRank - a.authorityRank);
}

// Self-service items — the universal surface every user has. The entries point
// at the /me/* self routes; their tenant-scope twins live in the İK Yönetimi
// group below (ADR-014 §2 no-copy: same Content components, server-side scope).
// They are split as the employee drawing groups them: what is about ME (Bana
// Özel), what the company shares with everyone (Şirket), and where to get help
// or change my own settings (Destek).
//
// LABELS: a twin pair may not share a label. A director is offered both halves
// at once, and "Organizasyon" twice told them nothing about which was which
// (measured 2026-08-01). The self half carries the possessive form the group
// already uses (Taleplerim / Zimmetlerim / Profilim); the tenant half keeps the
// plain noun. Applied to the three that still collided on 2026-08-04
// (me_tasks / me_leave_calendar / me_org — Görevlerim, İzin Takvimim,
// Organizasyonum), so the rule now holds for every entry; menu-config.test.ts
// pins the distinctness across the WHOLE rail in both bundles.
const SELF_SERVICE_ITEMS: readonly MenuItem[] = [
  // SEM-5131 — the self-history twin of the /requests decision inbox. Both
  // must be reachable from the single rail; they show different data
  // (mine=true self narrow vs tenant-scope management view).
  { key: "me-requests", labelKey: "menu.item.me_requests", href: "/me/requests", icon: Inbox },
  { key: "me-tasks", labelKey: "menu.item.me_tasks", href: "/me/tasks", icon: ListChecks },
  // "İzinlerim" has no page of its own: /me/leave and /me/requests both mount
  // <LeaveListContent /> with no props and rendered the identical "Taleplerim"
  // view (dev persona eng4, 2026-08-03; SEM-5557). The /me/leave route stays —
  // the calendar's list-view link resolves there.
  {
    key: "me-leave",
    labelKey: "menu.item.me_leave",
    icon: CalendarRange,
    children: [
      { key: "me-leave-balance", labelKey: "menu.item.me_leave_balance", href: "/me/leave/balance", icon: Hourglass },
      // Faz 8.6 / A3 — the monthly calendar view over the same leave window.
      { key: "me-leave-calendar", labelKey: "menu.item.me_leave_calendar", href: "/me/leave/calendar", icon: CalendarRange },
    ],
  },
  { key: "me-attendance", labelKey: "menu.item.me_attendance", href: "/me/attendance", icon: Clock },
  { key: "me-calendar", labelKey: "menu.item.me_calendar", href: "/me/calendar", icon: CalendarClock },
  { key: "me-scheduling", labelKey: "menu.item.me_scheduling", href: "/me/scheduling", icon: CalendarSearch },
  {
    key: "me-payroll",
    labelKey: "menu.item.me_payroll",
    href: "/me/payroll",
    icon: Wallet,
    children: [
      { key: "me-advances", labelKey: "advances.self_title", href: "/me/advances", icon: Wallet, requiredCapability: "payroll.read" },
      { key: "me-compensation", labelKey: "menu.item.me_compensation", href: "/me/compensation", icon: Wallet, requiredCapability: "payroll.read" },
      { key: "me-garnishments", labelKey: "menu.item.me_garnishments", href: "/me/garnishments", icon: Wallet, requiredCapability: "payroll.read" },
    ],
  },
  { key: "me-expenses", labelKey: "expenses.self_title", href: "/me/expenses", icon: Wallet, requiredCapability: "expenses.read" },
  { key: "me-cafeteria", labelKey: "cafeteria.title", href: "/me/cafeteria", icon: Utensils, requiredCapability: "cafeteria.read", requiredAccess: "cafeteria_self" },
  { key: "me-assets", labelKey: "menu.item.me_assets", href: "/me/assets", icon: Package },
  { key: "me-benefits", labelKey: "menu.item.me_benefits", href: "/me/benefits", icon: HandHeart },
  { key: "me-vehicle-requests", labelKey: "vehicle_requests.self_title", href: "/me/vehicle-requests", icon: Truck, requiredCapability: "vehicle_requests.read" },
  { key: "me-document-requests", labelKey: "document_requests.self_title", href: "/me/document-requests", icon: FileText, requiredCapability: "document_requests.read" },
  { key: "me-documents", labelKey: "menu.item.me_documents", href: "/me/documents", icon: FileText, requiredCapability: "forms.read" },
  // GET /v1/signature-requests rides the forms.read matrix cell (routes/signature-requests.ts, "closest catalog module").
  { key: "me-signature-requests", labelKey: "signature_requests.self_title", href: "/me/signature-requests", icon: FileSignature, requiredCapability: "forms.read" },
  { key: "me-staff-requisitions", labelKey: "staff_requisitions.menu.self", href: "/me/staff-requisitions", icon: UserPlus, requiredCapability: "staff_requisitions.read", requiredAccess: "staff_requisitions_self" },
  { key: "me-reviews", labelKey: "reviews.title", href: "/me/reviews", icon: CalendarRange, requiredCapability: "performance.read" },
  { key: "me-one-on-ones", labelKey: "one_on_one.title", href: "/me/one-on-ones", icon: CalendarRange, requiredCapability: "performance.read", requiredAccess: "one_on_ones_self" },
  { key: "me-onboarding", labelKey: "menu.item.me_onboarding", href: "/me/onboarding", icon: Rocket },
  { key: "me-buddies", labelKey: "onboarding.buddy.home_title", href: "/me/buddies", icon: Rocket, requiredCapability: "onboarding.read" },
  { key: "me-offboarding", labelKey: "offboarding.self.title", href: "/me/offboarding", icon: LogOut, requiredCapability: "offboarding.read" },
  { key: "me-profile", labelKey: "menu.item.me_profile", href: "/me/profile", icon: UserCircle },
];

const COMPANY_ITEMS: readonly MenuItem[] = [
  { key: "me-directory", labelKey: "menu.item.me_directory", href: "/me/directory", icon: Users },
  // Only a caller whose server scope reaches a subtree has reports to list.
  { key: "me-team", labelKey: "menu.item.me_team", href: "/me/team", icon: Network, requiredTier: "manager_subtree" },
  { key: "me-org", labelKey: "menu.item.me_org", href: "/me/org", icon: Network },
  { key: "me-announcements", labelKey: "menu.item.me_announcements", href: "/me/announcements", icon: Megaphone },
  { key: "me-policies", labelKey: "policies.title", href: "/me/policies", icon: BookOpen, requiredCapability: "policy.read", requiredAccess: "policies_self" },
  { key: "me-training", labelKey: "menu.item.me_training", href: "/me/training", icon: BookOpen, requiredCapability: "learning.read" },
  { key: "me-kudos", labelKey: "menu.item.me_kudos", href: "/me/kudos", icon: Award },
  { key: "me-surveys", labelKey: "menu.item.me_surveys", href: "/me/surveys", icon: ClipboardList },
  { key: "me-forms", labelKey: "forms.title", href: "/me/forms", icon: ClipboardList, requiredCapability: "forms.read" },
];

const SUPPORT_ITEMS: readonly MenuItem[] = [
  // P9/S7 — spec 70 employee surfaces: the Help Center (universal KB read) +
  // Destek Taleplerim (own tickets + new-ticket form). No requiredCapability:
  // every employee holds helpdesk read/create (employee_self floor); the
  // backend flag-gates the whole module (SUPPORT_HELPDESK_ENABLED → 503).
  { key: "help-center", labelKey: "menu.item.help_center", href: "/help", icon: LifeBuoy },
  { key: "me-support", labelKey: "menu.item.me_support", href: "/me/support", icon: MessageCircleQuestion },
  { key: "me-feedback", labelKey: "employee_feedback.rail", href: "/me/feedback", icon: MessageCircleQuestion, requiredCapability: "employee_feedback.read", requiredAccess: "feedback_self" },
  // SEM-6363 — the inbox is the employee_self page behind the TopBar bell.
  { key: "notifications", labelKey: "menu.item.notifications", href: "/notifications", icon: Bell },
  {
    key: "me-settings",
    labelKey: "menu.item.me_settings",
    icon: Settings,
    children: [
      { key: "me-notifications", labelKey: "menu.item.me_notifications", href: "/me/notifications", icon: Bell },
      // SEM-6835 — passkey'ler + bağlı kimlik sağlayıcılar. Yetenek kapısı YOK;
      // arkasındaki uç requireAuth ile korunur ve her çalışan kendi satırını okur.
      { key: "me-security", labelKey: "menu.item.me_security", href: "/me/security", icon: KeyRound },
    ],
  },
];

export const MENU_SECTIONS: readonly MenuSection[] = [
  // Ana Sayfa is the rail's anchor, not a group the caller "earns" — highest
  // rank keeps it above every headed group for everyone.
  {
    key: "overview",
    authorityRank: 100,
    items: [{ key: "home", labelKey: "menu.item.home", href: "/", icon: Home }],
  },
  {
    key: "self-service",
    sectionLabelKey: "menu.section.self_service",
    collapseMode: "none",
    authorityRank: 20,
    items: SELF_SERVICE_ITEMS,
  },
  { key: "company", sectionLabelKey: "menu.section.company", collapseMode: "none", authorityRank: 19, items: COMPANY_ITEMS },
  { key: "support", sectionLabelKey: "menu.section.support", collapseMode: "none", authorityRank: 18, items: SUPPORT_ITEMS },
  {
    key: "staff-requisitions-management", sectionLabelKey: "staff_requisitions.menu.manage", managementOnly: false, authorityRank: 38,
    items: [{ key: "staff-requisitions", labelKey: "staff_requisitions.menu.manage", href: "/staff-requisitions", icon: UserPlus, requiredCapability: "staff_requisitions.read", requiredAccess: "staff_requisitions_management" }],
  },
  {
    key: "employee-feedback-management", sectionLabelKey: "employee_feedback.inbox", managementOnly: false, authorityRank: 37,
    items: [
      { key: "employee-feedback", labelKey: "employee_feedback.inbox", href: "/employee-feedback", icon: Inbox, requiredCapability: "employee_feedback.read", requiredAccess: "feedback_review" },
      { key: "employee-feedback-routing", labelKey: "employee_feedback.routing", href: "/employee-feedback/routing", icon: GitBranch, requiredCapability: "employee_feedback.read", requiredAccess: "feedback_manage" },
      { key: "employee-feedback-categories", labelKey: "employee_feedback.categories", href: "/employee-feedback/categories", icon: FolderTree, requiredCapability: "employee_feedback.read", requiredAccess: "feedback_manage" },
      { key: "employee-feedback-identities", labelKey: "employee_feedback.identity_title", href: "/employee-feedback/identities", icon: ShieldCheck, requiredCapability: "employee_feedback.read", requiredAccess: "feedback_identity" },
      { key: "employee-feedback-report", labelKey: "employee_feedback.report", href: "/employee-feedback/report", icon: BarChart3, requiredCapability: "employee_feedback.read", requiredAccess: "feedback_manage" },
    ],
  },
  {
    key: "vehicle-requests-management", sectionLabelKey: "vehicle_requests.admin_title", managementOnly: false, authorityRank: 36,
    items: [{ key: "vehicle-requests", labelKey: "vehicle_requests.admin_title", href: "/vehicle-requests", icon: Truck, requiredCapability: "vehicle_requests.read", requiredAccess: "vehicle_requests_management" }],
  },
  {
    key: "document-requests-management", sectionLabelKey: "document_requests.admin_title", managementOnly: false, authorityRank: 34,
    items: [{ key: "document-requests", labelKey: "document_requests.admin_title", href: "/document-requests", icon: FileText, requiredCapability: "document_requests.read", requiredAccess: "document_requests_management" }],
  },
  // Same forms.read cell as the self entry. Below manager_subtree the route
  // narrows to the caller's own requests, which "İmza taleplerim" already shows.
  {
    key: "signature-requests-management", sectionLabelKey: "signature_requests.admin_title", managementOnly: true, authorityRank: 39,
    items: [{ key: "signature-requests", labelKey: "signature_requests.admin_title", href: "/signature-requests", icon: FileSignature, requiredCapability: "forms.read", requiredTier: "manager_subtree" }],
  },
  {
    key: "cafeteria-management", sectionLabelKey: "cafeteria.management", managementOnly: false, authorityRank: 35,
    items: [{ key: "cafeteria", labelKey: "cafeteria.management", href: "/cafeteria", icon: Utensils, requiredCapability: "cafeteria.read", requiredAccess: "cafeteria_management" }],
  },
  {
    key: "policies-management", sectionLabelKey: "policies.manage_title", managementOnly: false, authorityRank: 33,
    items: [{ key: "policies", labelKey: "policies.manage_title", href: "/policies", icon: BookOpen, requiredCapability: "policy.read", requiredAccess: "policies_management" }],
  },
  {
    key: "expenses-management", sectionLabelKey: "expenses.admin_title", managementOnly: false, authorityRank: 32,
    items: [{ key: "expenses", labelKey: "expenses.admin_title", href: "/expenses", icon: Wallet, requiredCapability: "expenses.read", requiredAccess: "expenses_management" }],
  },
  {
    key: "forms-management",
    sectionLabelKey: "forms.manage_title",
    authorityRank: 31,
    items: [{ key: "forms", labelKey: "forms.manage_title", href: "/forms", icon: ClipboardList, requiredCapability: "forms.read", requiredAccess: "forms_management" }],
  },
  {
    key: "payroll-management",
    sectionLabelKey: "menu.section.payroll_management",
    managementOnly: true,
    authorityRank: 40,
    collapseMode: "none",
    // Same capability + scope floor as the routes; job titles never grant access.
    items: [
      {
        key: "payroll",
        labelKey: "menu.item.payroll",
        href: "/payroll",
        icon: Banknote,
        requiredCapability: "payroll.read",
        requiredTier: "executive_broad",
      },
      // Sunucu okuma kapısı perm_payroll_read + perm_payroll_detail + executive_broad'dır; detail hücresi istemciye gelmediği için onu kademe temsil eder.
      // payroll.edit yalnız sayfadaki yazma düğmelerini açar; salt okuma yöneticisi listeyi ve ödeme belgesini görür (F10).
      { key: "payroll-advances", labelKey: "advances.admin_title", href: "/payroll/advances", icon: Wallet, requiredCapability: "payroll.read", requiredTier: "executive_broad" },
      { key: "payroll-exit", labelKey: "menu.item.payroll_exit", href: "/payroll/exit", icon: LogOut, requiredCapability: "payroll.create", requiredTier: "executive_broad" },
      { key: "payroll-employees", labelKey: "menu.item.payroll_employees", href: "/payroll/employees", icon: Users, requiredCapability: "payroll.edit", requiredTier: "executive_broad" },
      { key: "payroll-garnishments", labelKey: "menu.item.payroll_garnishments", href: "/payroll/garnishments", icon: Scale, requiredCapability: "payroll.edit", requiredTier: "executive_broad" },
      { key: "payroll-compensation", labelKey: "menu.item.compensation_changes", href: "/payroll/compensation", icon: Coins, requiredCapability: "payroll.edit", requiredTier: "executive_broad" },
      {
        key: "payroll-workplaces",
        labelKey: "menu.item.payroll_workplaces",
        href: "/payroll/workplaces",
        icon: Building2,
        requiredCapability: "payroll.edit",
        requiredTier: "executive_broad",
      },
    ],
  },
  {
    key: "hr-management",
    sectionLabelKey: "menu.section.hr_management",
    managementOnly: true,
    // Measured 2026-08-01 at 1440×720: with this group declared BELOW
    // self-service, its heading started at y=850 in a 1498px rail — an İK
    // Direktörü logged in and had to scroll to reach the group that is their
    // job. Ranking it above self-service is the fix, and it costs the plain
    // employee nothing: they never see this group at all (managementOnly).
    authorityRank: 30,
    items: [
      // learning.read is an every-employee cell (own assigned learning). The page
      // opens for authoring or assignment-reading authority, an exact grant the
      // cell cannot show, so the entry names the page's own predicate.
      { key: "learning", labelKey: "menu.item.learning", href: "/learning", icon: BookOpen, requiredCapability: "learning.read", requiredAccess: "learning_management" },
      { key: "people", labelKey: "menu.item.people", href: "/people", icon: Users, requiredCapability: "people.read" },
      // "İzinler" → /leave was REMOVED, not renamed: /leave and /requests
      // render the identical <LeaveListContent view="management" />, so the
      // rail advertised two destinations and delivered one decision inbox.
      // "Talepler" (/requests, below) owns that inbox; the leave section is
      // represented here by its two surfaces that are NOT the inbox. The
      // /leave route itself stays (bookmarks + the calendar's list-view
      // link resolve there); it is simply no longer a rail entry.
      { key: "leave-balances", labelKey: "menu.item.leave_balances", href: "/leave/balances", icon: Hourglass, requiredCapability: "leave.read" },
      { key: "leave-calendar", labelKey: "menu.item.leave_calendar", href: "/leave/calendar", icon: CalendarRange, requiredCapability: "leave.read" },
      { key: "pdks", labelKey: "menu.item.pdks", href: "/pdks", icon: Clock, requiredCapability: "time.approve" },
      { key: "org", labelKey: "menu.item.org", href: "/org", icon: Network, requiredCapability: "org.read" },
      { key: "assets", labelKey: "menu.item.assets", href: "/assets", icon: Package, requiredCapability: "assets.read" },
      { key: "benefits", labelKey: "menu.item.benefits", href: "/benefits", icon: HeartHandshake, requiredCapability: "benefits.read" },
      { key: "onboarding", labelKey: "menu.item.onboarding", href: "/onboarding", icon: Rocket, requiredCapability: "onboarding.read" },
      { key: "offboarding", labelKey: "offboarding.hub.title", href: "/offboarding", icon: LogOut, requiredCapability: "offboarding.read" },
      { key: "tasks", labelKey: "menu.item.tasks", href: "/tasks", icon: ListChecks, requiredCapability: "tasks.read" },
      // The list route checks requests.read (the calendar routes' matrix cell).
      { key: "events", labelKey: "menu.item.events", href: "/events", icon: CalendarDays, requiredCapability: "requests.read" },
      { key: "kudos", labelKey: "menu.item.kudos", href: "/kudos", icon: Award, requiredCapability: "recognition.read" },
      { key: "announcements", labelKey: "menu.item.announcements", href: "/announcements", icon: Megaphone, requiredCapability: "announcements.read" },
      // The page gates on the EE read roles (a JWT role set), not on this cell.
      { key: "surveys", labelKey: "menu.item.surveys", href: "/surveys", icon: BarChart3, requiredCapability: "surveys.read", requiredAccess: "survey_management" },
      { key: "experience", labelKey: "experience.title", href: "/experience", icon: ChartNoAxesCombined, requiredCapability: "surveys.read", requiredAccess: "survey_management" },
      // SEM-6331 — the spec-09 report engine had shipped complete (save · run ·
      // export · run ledger) with no caller and no rail entry. reports.read
      // floors at employee_self server-side, so capability alone would show
      // this to everyone; the group being managementOnly is what keeps the
      // builder an HR-management surface. The column ACL runs at plan time
      // regardless of who reaches the screen.
      { key: "reports", labelKey: "menu.item.reports", href: "/reports", icon: ChartNoAxesCombined, requiredCapability: "reports.read" },
      // P9/S7 — the agent ticket inbox. The TRUE agent boundary is the
      // role_support_agent-family JWT role, which is not a matrix cell, so it is
      // named as `support_agent`. helpdesk.approve stays as the floor: it was
      // the closest cell, and on its own it let every manager in, to a page that
      // says "only tickets you opened are listed here" (a copy of "Destek
      // Taleplerim", E23). The server row-narrows regardless (ADR-017).
      { key: "support-tickets", labelKey: "menu.item.support_tickets", href: "/support/tickets", icon: LifeBuoy, requiredCapability: "helpdesk.approve", requiredAccess: "support_agent" },
      { key: "requests", labelKey: "menu.item.requests", href: "/requests", icon: Inbox, requiredCapability: "requests.read" },
    ],
  },
  // "Ayarlar & Tanımlar" — the old Sistem group, widened into the setup surface
  // (SEM-7195). Setup screens were scattered: /settings had a rail entry, while
  // the asset catalogue and the SGK workplace list were reachable ONLY by
  // opening a sibling screen and clicking a tab. Same kind of destination,
  // three different ways to find it.
  {
    key: "setup",
    sectionLabelKey: "menu.section.setup",
    // The group folds as a whole and is born closed: these are screens a
    // tenant configures once and revisits rarely, so they earn a heading in the
    // rail but not permanent vertical space.
    collapseMode: "group",
    // Load-bearing, and NOT implied by the capability cells: `assets.read` is
    // held by every employee (own rows) and the catalogue is a tenant-wide
    // editor, so without this flag the group renders — with a write surface in
    // it — for a plain employee. AppShell.test.tsx witnesses exactly that.
    managementOnly: true,
    // Lowest rank even though these are the most privileged screens in the
    // product: setup is configuration a caller visits occasionally, not the
    // surface they work in. Ranking it by raw privilege would push İK Yönetimi
    // down again for exactly the caller this ordering exists to serve.
    authorityRank: 10,
    items: [
      // The settings hub stays ONE row: its tabs live inside the page and
      // listing them here would give every one of them a second address (owner
      // decision 2026-08-14). Whatever is NOT in that hub is what earns its own
      // row below.
      //
      // EVERY settings.read entry carries `requiredTier` (SEM-4780): the
      // backend tier map (require-permission.ts) puts the settings READ floor
      // at executive_broad, so a tenant granting perm_settings_read to a
      // role_manager/role_hr profile got a link that 403s on every call.
      {
        key: "settings",
        labelKey: "menu.item.settings",
        href: "/settings",
        icon: Settings,
        requiredCapability: "settings.read",
        requiredTier: "executive_broad",
      },
      // P5/B2 — Roller & İzinler (the rebuilt roles-permissions matrix SPA).
      // Gated settings.read (the iston Ayarlar surface); the rail hides it for
      // a caller without that capability, the route 403s regardless.
      {
        key: "roles",
        labelKey: "menu.item.roles",
        href: "/settings/roles",
        icon: ShieldCheck,
        requiredCapability: "settings.read",
        requiredTier: "executive_broad",
      },
      // W4b — the approval chain-builder (scr_approval_chain_builder). Gated
      // on the approval_flows.read catalog cell (the SAME matrix bit the
      // route's read preHandler checks) — the rail hides it for a caller
      // without that capability; the route 403s regardless (ADR-017).
      {
        key: "approval-chains",
        labelKey: "menu.item.approval_chains",
        href: "/settings/approval-chains",
        icon: GitBranch,
        requiredCapability: "approval_flows.read",
      },
      // The tenant's own asset categories and models. It had no rail entry at
      // all: the only way in was /assets → the "Katalog" tab.
      {
        key: "assets-catalog",
        labelKey: "menu.item.assets_catalog",
        href: "/assets/catalog",
        icon: Boxes,
        requiredCapability: "assets.read",
      },
      {
        key: "leave-types",
        labelKey: "menu.item.leave_types",
        href: "/settings/leave-types",
        icon: CalendarRange,
        requiredCapability: "leave.read",
      },
      {
        key: "cost-centers",
        labelKey: "menu.item.cost_centers",
        href: "/settings/cost-centers",
        icon: Coins,
        requiredCapability: "settings.read",
        requiredTier: "executive_broad",
      },
      {
        key: "departments",
        labelKey: "menu.item.departments",
        href: "/settings/departments",
        icon: FolderTree,
        requiredCapability: "hris.read",
      },
      {
        key: "job-catalogue",
        labelKey: "menu.item.job_catalogue",
        href: "/settings/job-catalogue",
        icon: Tags,
        requiredCapability: "hris.read",
      },
      {
        key: "locations",
        labelKey: "menu.item.locations",
        href: "/settings/locations",
        icon: MapPin,
        requiredCapability: "settings.read",
        requiredTier: "executive_broad",
      },
      {
        key: "devices",
        labelKey: "menu.item.devices",
        href: "/settings/devices",
        icon: Smartphone,
        // The device writes ride hris.edit at manager_subtree (routes/devices.ts);
        // below that tier the list is narrowed to the caller's own device.
        requiredCapability: "hris.edit",
        requiredTier: "manager_subtree",
      },
      {
        key: "holidays",
        labelKey: "menu.item.holidays",
        href: "/settings/holidays",
        icon: CalendarDays,
        requiredCapability: "settings.read",
        requiredTier: "executive_broad",
      },
      {
        key: "schedules",
        labelKey: "menu.item.schedules",
        href: "/settings/schedules",
        icon: CalendarClock,
        requiredCapability: "time.read",
        // Yayımlama ucu executive_broad taban ister (SEM-6442: yük tüm roster);
        // manager_subtree bir çağıran vardiya listesinin yalnız kendi alt
        // ağacını görür ve gördüğünden fazlasını yayımlamış olurdu.
        requiredTier: "executive_broad",
      },
      {
        key: "org_dimensions",
        labelKey: "menu.item.org_dimensions",
        href: "/settings/org-dimensions",
        icon: Layers,
        requiredCapability: "settings.read",
        requiredTier: "executive_broad",
      },
    ],
  },
];

/**
 * The manifest entry a URL belongs to: the entry whose `href` is the path or
 * its LONGEST ancestor, matched on a segment boundary ("/settings/roles" is the
 * roles entry, "/settings/users" falls through to the settings hub, and
 * "/peoples" matches nothing). Lets a route guard reuse the rail's own answer
 * without repeating a capability / tier pair next to each route.
 */
export function menuEntryKeyForPath(pathname: string): string | undefined {
  let best: MenuLink | undefined;
  for (const section of MENU_SECTIONS) {
    for (const item of menuLinks(section.items)) {
      if (item.href === "/") continue;
      const covers = pathname === item.href || pathname.startsWith(`${item.href}/`);
      if (covers && (best === undefined || item.href.length > best.href.length)) best = item;
    }
  }
  return best?.key;
}
